import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import { AutoComplete, type AutoCompleteProps } from "antd"
import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { searchCustomers } from "../../containers/CustomersPage/redux/customers.thunks.ts"
import { type CustomerSearchResult } from "../../containers/CustomersPage/types/customer.types.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useDebouncedCallback } from "../../hooks/use-debounced-callback.ts"

const MIN_QUERY_LENGTH = 3
const PAGE_SIZE = 20

export const getCustomerDisplayName = (customer: CustomerSearchResult) =>
  [customer.first_name, customer.middle_name, customer.last_name]
    .filter(Boolean)
    .join(" ") ||
  customer.email ||
  customer.phone ||
  ""

interface CustomerAutoCompleteProps {
  onSelect: (customer: CustomerSearchResult) => void
  onClear: () => void
  disabled?: boolean
}

export function CustomerAutoComplete({
  onSelect,
  onClear,
  disabled
}: CustomerAutoCompleteProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [value, setValue] = useState("")
  const [customers, setCustomers] = useState<CustomerSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const latestQuery = useRef(0)

  // A customer can carry none of the three handles — phone search is what
  // surfaces them — and a blank row is neither readable nor selectable.
  const displayName = (customer: CustomerSearchResult) =>
    getCustomerDisplayName(customer) || t("tabbedForm.customerSearch.unnamed")

  // Options are keyed by position, so a response landing out of order would not
  // just show stale rows — it would repoint the index the click is about to use.
  // Discarding the results also has to discard the request still in flight.
  const discardResults = () => {
    latestQuery.current += 1
    setCustomers([])
    setLoading(false)
  }

  const handleSearch = async (search: string) => {
    if (search.length < MIN_QUERY_LENGTH) {
      discardResults()
      return
    }

    const seq = ++latestQuery.current

    setLoading(true)

    try {
      const result = await dispatch(
        searchCustomers({ q: search, page: 1, page_size: PAGE_SIZE })
      ).unwrap()

      if (seq !== latestQuery.current) return

      setCustomers(Array.isArray(result) ? result : result?.results || [])
    } catch (e) {
      if (seq !== latestQuery.current) return

      console.error(e)
      setCustomers([])
    } finally {
      if (seq === latestQuery.current) setLoading(false)
    }
  }

  const debounced = useDebouncedCallback(
    (search: string) => handleSearch(search.trim()),
    500
  )

  const handleInput = (search: string) => {
    setValue(search)
    debounced(search)
  }

  // The option value stays positional so the visible control never exposes a
  // UUID; the selected record itself carries the stable customer identity.
  const handleSelect = (position: string) => {
    const selected = customers[Number(position)]
    if (!selected) return

    setValue(displayName(selected))
    onSelect(selected)
  }

  const handleClear = () => {
    setValue("")
    discardResults()
    onClear()
  }

  const options: AutoCompleteProps["options"] = customers.map(
    (customer, position) => ({
      value: String(position),
      label: (
        <div className={"flex flex-col gap-0.5"}>
          <span className={"font-medium"}>{displayName(customer)}</span>
          <span className={"text-xs text-black/45"}>
            {[customer.email, customer.phone].filter(Boolean).join(" · ")}
          </span>
        </div>
      )
    })
  )

  return (
    <AutoComplete
      allowClear
      className={"w-full"}
      disabled={disabled}
      value={value}
      options={options}
      onSearch={handleInput}
      onSelect={handleSelect}
      onClear={handleClear}
      placeholder={t("tabbedForm.customerSearch.placeholder")}
      notFoundContent={
        loading
          ? t("tabbedForm.customerSearch.searching")
          : value.trim().length < MIN_QUERY_LENGTH
            ? t("tabbedForm.customerSearch.minLength", {
                count: MIN_QUERY_LENGTH
              })
            : t("tabbedForm.customerSearch.notFound")
      }
      prefix={<SearchOutlined />}
      suffixIcon={loading ? <LoadingOutlined /> : null}
    />
  )
}
